import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMap,
} from '@vis.gl/react-google-maps';
import { useDevice, useSensorData, useFieldPending, useMTCommand } from '@/hooks';
import { NAMTMessageType } from '@/types/enums';
import { Button } from '@/components/common/Button/Button';
import { StatusBadge } from '@/components/common/StatusBadge/StatusBadge';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import { PendingBadge } from '@/components/common/ConfigControls';
import { findPendingMT } from '@/lib/sensor-commands';
import {
  formatCoordinate,
  formatFeet,
  feetToMeters,
} from '@/lib/formatters';
import { env } from '@/config/env';
import { useEffect, useRef } from 'react';

// ---------------------------------------------------------------------------
// DeviceGeofenceCircle -- draws a single circle for the device geofence
// ---------------------------------------------------------------------------
function DeviceGeofenceCircle({
  lat,
  lng,
  radiusFt,
  alertPresent,
}: {
  lat: number;
  lng: number;
  radiusFt: number;
  alertPresent: boolean;
}) {
  const map = useMap();
  const circleRef = useRef<google.maps.Circle | null>(null);

  useEffect(() => {
    circleRef.current?.setMap(null);
    circleRef.current = null;

    if (!map) return;

    const color = alertPresent ? '#EF4444' : '#22C55E';
    circleRef.current = new google.maps.Circle({
      center: { lat, lng },
      radius: feetToMeters(radiusFt),
      fillColor: color,
      fillOpacity: 0.12,
      strokeColor: color,
      strokeOpacity: 0.6,
      strokeWeight: 2,
      map,
    });

    // Fit map to circle bounds
    const bounds = circleRef.current.getBounds();
    if (bounds) {
      map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
    }

    return () => {
      circleRef.current?.setMap(null);
      circleRef.current = null;
    };
  }, [map, lat, lng, radiusFt, alertPresent]);

  return null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function DeviceGeofencePage() {
  const { deviceUUID } = useParams<{ deviceUUID: string }>();
  const { selectedDeviceInfo, deviceInfoLoading } = useDevice();
  const { sensorData } = useSensorData();

  const [toggling, setToggling] = useState(false);
  const fieldPending = useFieldPending();
  const { sendCommand, mtObjList, feedback, setFeedback } = useMTCommand(deviceUUID ?? '');

  const deviceGeofences = sensorData?.geofenceList ?? [];
  const geo = deviceGeofences[0] ?? null;
  const enabled = geo ? fieldPending.getOptimisticValue<boolean>('geofenceEnabled') ?? geo.enabled : false;
  const enabledPending =
    fieldPending.isPending('geofenceEnabled') ||
    !!findPendingMT(mtObjList, NAMTMessageType.SET_GEOFENCE_ENABLED) ||
    !!findPendingMT(mtObjList, NAMTMessageType.SET_GEOFENCE_DISABLED);

  const handleToggle = async () => {
    if (!deviceUUID || !geo) return;
    setToggling(true);
    setFeedback(null);
    try {
      await fieldPending.run('geofenceEnabled', !enabled, () =>
        sendCommand(enabled ? NAMTMessageType.SET_GEOFENCE_DISABLED : NAMTMessageType.SET_GEOFENCE_ENABLED),
      );
    } finally {
      setToggling(false);
    }
  };

  if (deviceInfoLoading && !selectedDeviceInfo) return <PageSpinner />;

  if (!selectedDeviceInfo) {
    return (
      <EmptyState
        title="No Device Selected"
        description="Select a device to view device geofence."
      />
    );
  }

  if (deviceGeofences.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Device Geofence</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Onboard geofence monitoring for your vessel
          </p>
        </div>
        <EmptyState
          title="No Device Geofence"
          description="This device does not have an onboard geofence configured."
          icon={
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
            </svg>
          }
        />
      </div>
    );
  }

  const hasValidPosition = geo && (geo.refLat !== 0 || geo.refLon !== 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Device Geofence</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Onboard geofence monitoring for your vessel
        </p>
      </div>

      {feedback && (
        <div
          className={`rounded-lg border p-3 text-sm ${
            feedback.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400'
              : 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400'
          }`}
        >
          <div className="flex items-center justify-between">
            <span>{feedback.message}</span>
            <button type="button" onClick={() => setFeedback(null)} className="ml-3 text-xs font-medium underline">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Status & Controls */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 dark:border-navy-700 dark:bg-navy-900">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-white">Geofence Status</h2>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Controls the device's onboard geofence monitoring
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge
              label={enabled ? 'Enabled' : 'Disabled'}
              variant={enabled ? 'success' : 'neutral'}
              dot
            />
            {enabledPending && <PendingBadge />}
            {geo.alertPresent && (
              <StatusBadge label="Alert" variant="danger" dot />
            )}
            <Button
              size="sm"
              variant={enabled ? 'danger' : 'primary'}
              loading={toggling}
              onClick={handleToggle}
            >
              {enabled ? 'Disable' : 'Enable'}
            </Button>
          </div>
        </div>
      </div>

      {/* Auto-arm note */}
      {selectedDeviceInfo?.globalSettings?.geoAutoArmSetting != null &&
        (selectedDeviceInfo.globalSettings.geoAutoArmSetting as unknown) !== 0 &&
        selectedDeviceInfo.globalSettings.geoAutoArmSetting !== 'GEOFENCE_AUTO_ARM_DISARM_DISABLED' && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
          <div className="flex items-center gap-2">
            <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
            </svg>
            The geofence will automatically activate when the security system is armed and deactivate when it is disarmed. Configure this behavior under Settings &gt; Geofence Sync.
          </div>
        </div>
      )}

      {/* Map */}
      {enabled && hasValidPosition && (
        <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-navy-700">
          <APIProvider apiKey={env.GOOGLE_MAPS_API_KEY}>
            <Map
              mapId="device-geofence-map"
              defaultCenter={{ lat: geo.refLat, lng: geo.refLon }}
              defaultZoom={15}
              style={{ height: '400px', width: '100%' }}
              gestureHandling="greedy"
              disableDefaultUI={false}
              mapTypeId="hybrid"
            >
              <DeviceGeofenceCircle
                lat={geo.refLat}
                lng={geo.refLon}
                radiusFt={geo.radius}
                alertPresent={geo.alertPresent}
              />
              {/* Center marker */}
              <AdvancedMarker
                position={{ lat: geo.refLat, lng: geo.refLon }}
                zIndex={100}
              >
                <div className="flex flex-col items-center">
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: geo.alertPresent ? '#EF4444' : '#22C55E',
                      border: '1.5px solid #fff',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    }}
                  />
                  <span className="mt-0.5 whitespace-nowrap rounded bg-white/90 px-1 py-0.5 text-[10px] font-medium text-slate-700 shadow-sm dark:bg-navy-800/90 dark:text-slate-300">
                    Center
                  </span>
                </div>
              </AdvancedMarker>

              {/* Vessel position */}
              {selectedDeviceInfo?.gpsLocationData?.locationValid && (
                <AdvancedMarker
                  position={{
                    lat: selectedDeviceInfo.gpsLocationData.latitude,
                    lng: selectedDeviceInfo.gpsLocationData.longitude,
                  }}
                  zIndex={1000}
                >
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      backgroundColor: '#06B6D4',
                      border: '3px solid #fff',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                      }}
                    />
                  </div>
                </AdvancedMarker>
              )}
            </Map>
          </APIProvider>
        </div>
      )}

      {/* Details */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 dark:border-navy-700 dark:bg-navy-900">
        <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-white">Geofence Details</h2>
        {!hasValidPosition ? (
          <p className="text-sm text-slate-400 dark:text-slate-500">
            No geofence position has been set on this device.
          </p>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500 dark:text-slate-400">Center</span>
              <span className="font-mono text-slate-700 dark:text-slate-300">
                {formatCoordinate(geo.refLat, geo.refLon)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500 dark:text-slate-400">Radius</span>
              <span className="text-slate-700 dark:text-slate-300">
                {formatFeet(geo.radius)}
              </span>
            </div>
            {geo.displacementDataValid && (
              <div className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-600 dark:bg-navy-800 dark:text-slate-400">
                Vessel is{' '}
                <span className="font-semibold text-slate-800 dark:text-slate-200">
                  {formatFeet(geo.displacementFeet)}
                </span>{' '}
                heading{' '}
                <span className="font-semibold text-slate-800 dark:text-slate-200">
                  {geo.displacementHeading}&deg;
                </span>{' '}
                from center
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
